import React, { useState } from 'react';
import { Flight } from '../types/aircraft';
import flightService, { CreateFlightRequest } from '../services/flightService';
import './FlightSelector.css';

interface FlightSelectorProps {
  flightInputMode: 'ready' | 'manual';
  onFlightInputModeChange: (mode: 'ready' | 'manual') => void;
  flights: Flight[];
  selectedFlightId: number;
  onFlightSelect: (flightId: number) => void;
  manualFlightData: {
    flightNumber: string;
    departureAirport: string;
    arrivalAirport: string;
    departureTime: string;
    arrivalTime: string;
  };
  onManualFlightChange: (field: string, value: string) => void;
  onFlightsUpdate?: () => void; // Callback для обновления списка рейсов
}

const FlightSelector: React.FC<FlightSelectorProps> = ({
  flightInputMode,
  onFlightInputModeChange,
  flights,
  selectedFlightId,
  onFlightSelect,
  manualFlightData,
  onManualFlightChange,
  onFlightsUpdate
}) => {
  const [isCreatingFlight, setIsCreatingFlight] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreateAndSelectFlight = async () => {
    if (!manualFlightData.flightNumber || !manualFlightData.departureAirport || 
        !manualFlightData.arrivalAirport || !manualFlightData.departureTime || 
        !manualFlightData.arrivalTime) {
      setCreateError('Пожалуйста, заполните все поля');
      return;
    }

    try {
      setIsCreatingFlight(true);
      setCreateError(null);

      const newFlightData: CreateFlightRequest = {
        flightNumber: manualFlightData.flightNumber,
        departureAirport: manualFlightData.departureAirport,
        arrivalAirport: manualFlightData.arrivalAirport,
        departureTime: manualFlightData.departureTime,
        arrivalTime: manualFlightData.arrivalTime,
        aircraftTypeId: 1, // По умолчанию
        carrierId: 1, // По умолчанию
        statusId: 1 // По умолчанию
      };

      const newFlight = await flightService.createFlight(newFlightData);
      
      // Обновляем список рейсов
      if (onFlightsUpdate) {
        onFlightsUpdate();
      }

      // Автоматически выбираем созданный рейс
      onFlightSelect(newFlight.id);
      
      // Переключаемся на режим готовых рейсов
      onFlightInputModeChange('ready');

      // Очищаем форму
      onManualFlightChange('flightNumber', '');
      onManualFlightChange('departureAirport', '');
      onManualFlightChange('arrivalAirport', '');
      onManualFlightChange('departureTime', '');
      onManualFlightChange('arrivalTime', '');

    } catch (error) {
      console.error('Error creating flight:', error);
      setCreateError('Ошибка при создании рейса. Проверьте данные и попробуйте снова.');
    } finally {
      setIsCreatingFlight(false);
    }
  };

  return (
    <div className="form-section">
      <h3>Ввод рейса</h3>
      
      <div className="flight-input-toggle">
        <button
          type="button"
          className={`toggle-button ${flightInputMode === 'ready' ? 'active' : ''}`}
          onClick={() => onFlightInputModeChange('ready')}
        >
          Готовые рейсы
        </button>
        <button
          type="button"
          className={`toggle-button ${flightInputMode === 'manual' ? 'active' : ''}`}
          onClick={() => onFlightInputModeChange('manual')}
        >
          Ручной ввод
        </button>
      </div>

      {flightInputMode === 'ready' ? (
        <div className="flight-selection">
          <h4>Выберите рейс:</h4>
          <div className="flights-list-container">
            <div className="flights-list">
              {flights.map((flight) => (
                <div key={flight.id} className="flight-card">
                  <div className="flight-info">
                    <div className="flight-number">{flight.flightNumber}</div>
                    <div className="flight-route">
                      {flight.departureAirport} → {flight.arrivalAirport}
                    </div>
                    <div className="flight-date">
                      {new Date(flight.departureTime).toLocaleDateString('ru-RU')}
                    </div>
                  </div>
                  <input
                    type="radio"
                    name="flightId"
                    value={flight.id}
                    checked={selectedFlightId === flight.id}
                    onChange={(e) => onFlightSelect(Number(e.target.value))}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="manual-flight-input">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="flightNumber">Номер рейса *</label>
              <input
                type="text"
                id="flightNumber"
                value={manualFlightData.flightNumber}
                onChange={(e) => onManualFlightChange('flightNumber', e.target.value)}
                placeholder="SU1234"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="departureAirport">Аэропорт вылета *</label>
              <input
                type="text"
                id="departureAirport"
                value={manualFlightData.departureAirport}
                onChange={(e) => onManualFlightChange('departureAirport', e.target.value)}
                placeholder="Астана"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="arrivalAirport">Аэропорт прилета *</label>
              <input
                type="text"
                id="arrivalAirport"
                value={manualFlightData.arrivalAirport}
                onChange={(e) => onManualFlightChange('arrivalAirport', e.target.value)}
                placeholder="Алматы"
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="departureTime">Время вылета *</label>
              <input
                type="datetime-local"
                id="departureTime"
                value={manualFlightData.departureTime}
                onChange={(e) => onManualFlightChange('departureTime', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="arrivalTime">Время прилета *</label>
              <input
                type="datetime-local"
                id="arrivalTime"
                value={manualFlightData.arrivalTime}
                onChange={(e) => onManualFlightChange('arrivalTime', e.target.value)}
                required
              />
            </div>
          </div>

          {createError && (
            <div className="error-message">
              {createError}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="create-flight-btn"
              onClick={handleCreateAndSelectFlight}
              disabled={isCreatingFlight}
            >
              {isCreatingFlight ? 'Создание...' : 'Создать и выбрать рейс'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightSelector; 